/**
 * API Endpoints Configuration
 * Centralized endpoint definitions maintaining exact same relative URLs
 * Follows SOC: Separation of concerns - URLs separated from business logic
 */

// List of endpoints that don't require authentication
export const PUBLIC_ENDPOINTS = [
  "/public-config",
  "/auth/login/access-token",
  "/auth/signup",
  "/auth/password-recovery",
  "/auth/reset-password",
  "/auth/verify-email",
  "/auth/resend-verification-email",
  // Google OAuth endpoints - these are part of the authentication flow itself
  "/auth/google/callback",
  "/auth/google/login",
  "/auth/google/exchange-token",
] as const;

/**
 * Authentication endpoints
 */
export const AUTH_ENDPOINTS = {
  LOGIN: "/auth/login/access-token",
  SIGNUP: "/auth/signup",
  VERIFY_EMAIL: "/auth/verify-email",
  PASSWORD_RECOVERY: "/auth/password-recovery",
  RESET_PASSWORD: "/auth/reset-password",
  RESEND_VERIFICATION: "/auth/resend-verification-email",
} as const;

/**
 * User management endpoints
 */
export const USER_ENDPOINTS = {
  ME: "/users/me",
  SEARCH: "/users/search",
} as const;

/**
 * Agent management endpoints
 */
export const AGENT_ENDPOINTS = {
  LIST: "/agents/",
  DEFAULT: "/agents/default-agent",
  BY_ID: (agentId: string) => `/agents/${agentId}`,
  SESSIONS: (agentId: string) => `/agents/${agentId}/sessions`,
  SESSION_BY_ID: (agentId: string, sessionId: string) =>
    `/agents/${agentId}/sessions/${sessionId}`,
  BULK_DELETE_SESSIONS: (agentId: string) =>
    `/agents/${agentId}/bulk-operations/delete-all-sessions`,
  // Corpus management
  CORPUS_FILES: (agentId: string) => `/agents/${agentId}/corpus/files`,
  CORPUS_VALIDATE: (agentId: string) => `/agents/${agentId}/corpus/validate`,
  CORPUS_SUGGESTIONS: (agentId: string) =>
    `/agents/${agentId}/corpus/suggestions`,
  // Ingestion
  INGEST_ASYNC: (agentId: string) => `/agents/${agentId}/ingest/async`,
  INGEST_STATUS: (agentId: string) => `/agents/${agentId}/ingest/status`,
  INGEST_CLEAN: (agentId: string) => `/agents/${agentId}/ingest/clean`,
  PRESIGNED_URL: (agentId: string) => `/agents/${agentId}/ingest/presigned-url`,
  PRESIGNED_POST: (agentId: string) =>
    `/agents/${agentId}/ingest/presigned-post`,
  // Note: UPLOAD_COMPLETE was removed - backend endpoint doesn't exist
  // The /ingest/async endpoint handles ingestion after S3 upload
} as const;

/**
 * Agent capability catalog endpoints (C1c).
 *
 * Authoritative source for Chat model selection (and, later, the Work card
 * editor). Agent-scoped and viewer-authorized; no request body.
 */
export const AGENT_CAPABILITIES_ENDPOINTS = {
  CAPABILITIES: (agentId: string) =>
    `/agents/${encodeURIComponent(agentId)}/capabilities`,
  /** Chat-scoped models only (agent.chat), for the Chat model selector (L7). */
  CHAT_MODELS: (agentId: string) =>
    `/agents/${encodeURIComponent(agentId)}/chat-models`,
} as const;

/**
 * Workflow management + workflow-session endpoints (Phase F5a).
 *
 * Additive family mirroring the committed backend routes under
 * `/agents/{agent_id}/workflows*` and `/agents/{agent_id}/workflow-sessions*`.
 * Every path identifier is URL-encoded exactly once here so services never
 * assemble raw workflow URLs. The streaming execute path is intentionally NOT
 * an Axios endpoint — it is issued through the Next.js stream proxy by
 * `workflow-execution-service.ts` (see `EXECUTE_MESSAGE_PATH`).
 */
export const WORKFLOW_ENDPOINTS = {
  // --- Workflow envelope ---------------------------------------------------- //
  WORKFLOWS: (agentId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows`,
  WORKFLOW_BY_ID: (agentId: string, workflowId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}`,
  WORKFLOW_ARCHIVE: (agentId: string, workflowId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}/archive`,
  WORKFLOW_ACTIVE_VERSION: (agentId: string, workflowId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}/active-version`,

  // --- Immutable versions --------------------------------------------------- //
  VERSIONS: (agentId: string, workflowId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}/versions`,
  VERSION_BY_ID: (agentId: string, workflowId: string, versionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}/versions/${encodeURIComponent(versionId)}`,
  VERSION_VALIDATE: (agentId: string, workflowId: string, versionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}/versions/${encodeURIComponent(versionId)}/validate`,
  VERSION_COMPILE: (agentId: string, workflowId: string, versionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}/versions/${encodeURIComponent(versionId)}/compile`,

  // --- Python candidates (H2) ------------------------------------------------ //
  PYTHON_CANDIDATE_PIN: (agentId: string, workflowId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}/python-candidates/pin`,
  // One-click pin of a completed generated execution into a new version (H2.2).
  PYTHON_CANDIDATE_PIN_SCRIPT: (agentId: string, workflowId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}/python-candidates/pin-script`,
  // Read a pinned script resource's source for editor inspection (H2.2).
  PYTHON_SCRIPT_RESOURCE: (
    agentId: string,
    workflowId: string,
    artifactId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}/script-resources/${encodeURIComponent(artifactId)}`,
  PYTHON_CANDIDATES: (agentId: string, sessionId: string, attemptId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/attempts/${encodeURIComponent(attemptId)}/python-candidates`,
  PYTHON_CANDIDATE_CONTENT: (
    agentId: string,
    sessionId: string,
    attemptId: string,
    artifactId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/attempts/${encodeURIComponent(attemptId)}/python-candidates/${encodeURIComponent(
      artifactId
    )}/content`,

  // --- Pinned workflow sessions --------------------------------------------- //
  VERSION_SESSIONS: (agentId: string, workflowId: string, versionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(
      workflowId
    )}/versions/${encodeURIComponent(versionId)}/sessions`,
  WORKFLOW_SESSIONS: (agentId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions`,
  WORKFLOW_SESSION_BY_ID: (agentId: string, sessionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}`,

  // --- Durable execution attempts (G1) --------------------------------------- //
  WORKFLOW_SESSION_ATTEMPTS: (agentId: string, sessionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/attempts`,
  WORKFLOW_SESSION_ATTEMPT_BY_ID: (
    agentId: string,
    sessionId: string,
    attemptId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/attempts/${encodeURIComponent(attemptId)}`,
  WORKFLOW_SESSION_ATTEMPT_USAGE: (
    agentId: string,
    sessionId: string,
    attemptId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/attempts/${encodeURIComponent(attemptId)}/usage`,
  WORKFLOW_SESSION_STEP_COMPARISON: (
    agentId: string,
    sessionId: string,
    attemptId: string,
    nodeId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/attempts/${encodeURIComponent(attemptId)}/usage/step-comparison/${encodeURIComponent(
      nodeId
    )}`,
  WORKFLOW_SESSION_STEP_COMPARISON_VERSION: (
    agentId: string,
    sessionId: string,
    attemptId: string,
    nodeId: string,
    versionId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/attempts/${encodeURIComponent(attemptId)}/usage/step-comparison/${encodeURIComponent(
      nodeId
    )}/versions/${encodeURIComponent(versionId)}`,
  WORKFLOW_SESSION_ATTEMPT_EVENTS: (
    agentId: string,
    sessionId: string,
    attemptId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/attempts/${encodeURIComponent(attemptId)}/events`,
  WORKFLOW_SESSION_ATTEMPT_CANCEL: (
    agentId: string,
    sessionId: string,
    attemptId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/attempts/${encodeURIComponent(attemptId)}/cancel`,

  // --- Workflow artifacts (H1) ----------------------------------------------- //
  WORKFLOW_SESSION_ARTIFACTS: (agentId: string, sessionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/artifacts`,
  WORKFLOW_SESSION_ARTIFACT_BY_ID: (
    agentId: string,
    sessionId: string,
    artifactId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/artifacts/${encodeURIComponent(artifactId)}`,
  WORKFLOW_SESSION_ARTIFACT_DOWNLOAD: (
    agentId: string,
    sessionId: string,
    artifactId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/artifacts/${encodeURIComponent(artifactId)}/download`,
  WORKFLOW_SESSION_ATTEMPT_ARTIFACTS: (
    agentId: string,
    sessionId: string,
    attemptId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/attempts/${encodeURIComponent(attemptId)}/artifacts`,
  CHAT_SESSION_ARTIFACT_DOWNLOAD: (
    agentId: string,
    chatSessionId: string,
    artifactId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/chat-sessions/${encodeURIComponent(
      chatSessionId
    )}/artifacts/${encodeURIComponent(artifactId)}/download`,
  CHAT_SESSION_ARTIFACTS: (agentId: string, chatSessionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/chat-sessions/${encodeURIComponent(
      chatSessionId
    )}/artifacts`,
  CHAT_SESSION_ARTIFACT_BY_ID: (
    agentId: string,
    chatSessionId: string,
    artifactId: string
  ) =>
    `/agents/${encodeURIComponent(agentId)}/chat-sessions/${encodeURIComponent(
      chatSessionId
    )}/artifacts/${encodeURIComponent(artifactId)}`,

  // --- Agent template artifacts (H1.1) --------------------------------------- //
  AGENT_TEMPLATE_ARTIFACTS: (agentId: string) =>
    `/agents/${encodeURIComponent(agentId)}/template-artifacts`,
  AGENT_TEMPLATE_ARTIFACT_BY_ID: (agentId: string, artifactId: string) =>
    `/agents/${encodeURIComponent(agentId)}/template-artifacts/${encodeURIComponent(
      artifactId
    )}`,
  AGENT_TEMPLATE_ARTIFACT_DOWNLOAD: (agentId: string, artifactId: string) =>
    `/agents/${encodeURIComponent(agentId)}/template-artifacts/${encodeURIComponent(
      artifactId
    )}/download`,

  // --- Client (browser) Python execution jobs (H2.1) ------------------------- //
  CLIENT_PYTHON_JOB: (jobId: string) =>
    `/client-python-jobs/${encodeURIComponent(jobId)}`,
  CLIENT_PYTHON_JOB_CANDIDATE: (jobId: string) =>
    `/client-python-jobs/${encodeURIComponent(jobId)}/candidate`,
  CLIENT_PYTHON_JOB_LEASE: (jobId: string) =>
    `/client-python-jobs/${encodeURIComponent(jobId)}/lease`,
  CLIENT_PYTHON_JOB_RUNNING: (jobId: string) =>
    `/client-python-jobs/${encodeURIComponent(jobId)}/running`,
  CLIENT_PYTHON_JOB_RESULT: (jobId: string) =>
    `/client-python-jobs/${encodeURIComponent(jobId)}/result`,
  CLIENT_PYTHON_JOB_FAIL: (jobId: string) =>
    `/client-python-jobs/${encodeURIComponent(jobId)}/fail`,
  CLIENT_PYTHON_JOB_CANCEL: (jobId: string) =>
    `/client-python-jobs/${encodeURIComponent(jobId)}/cancel`,
  CLIENT_PYTHON_JOB_RESUME: (sessionId: string, jobId: string) =>
    `/chat-sessions/${encodeURIComponent(sessionId)}/client-executions/${encodeURIComponent(
      jobId
    )}/resume`,
  CLIENT_PYTHON_SESSION_EXECUTIONS: (sessionId: string) =>
    `/chat-sessions/${encodeURIComponent(sessionId)}/client-executions`,
  // H2.2 workflow browser Python: the job-scoped lease/candidate/running/
  // result/fail/cancel endpoints above are context-agnostic (the backend
  // dispatches by job context), so only the workflow-session-scoped resume and
  // recovery-list routes are distinct.
  WORKFLOW_CLIENT_PYTHON_RESUME: (sessionId: string, jobId: string) =>
    `/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/client-executions/${encodeURIComponent(jobId)}/resume`,
  WORKFLOW_CLIENT_PYTHON_EXECUTIONS: (sessionId: string) =>
    `/workflow-sessions/${encodeURIComponent(sessionId)}/client-executions`,

  // --- Agent generic resource artifacts (H1.2) ------------------------------- //
  AGENT_RESOURCE_ARTIFACTS: (agentId: string) =>
    `/agents/${encodeURIComponent(agentId)}/resource-artifacts`,
  AGENT_RESOURCE_ARTIFACT_BY_ID: (agentId: string, artifactId: string) =>
    `/agents/${encodeURIComponent(agentId)}/resource-artifacts/${encodeURIComponent(
      artifactId
    )}`,

  // --- Native-tool permissions ---------------------------------------------- //
  TOOL_PERMISSIONS: (agentId: string) =>
    `/agents/${encodeURIComponent(agentId)}/tool-permissions`,

  /**
   * Grant-backed enable/disable of ONE capability-only Tool (L7): enabling
   * delegates the developer's own delegable capability into the Agent, disabling
   * revokes it. The exposure gate rides along in the same transaction.
   */
  TOOL_PERMISSION_ITEM: (agentId: string, toolKey: string) =>
    `/agents/${encodeURIComponent(agentId)}/tool-permissions/${encodeURIComponent(
      toolKey
    )}`,

  /** Canonical effective + grantable authority per capability-only Tool (L7). */
  TOOL_AUTHORITY: (agentId: string) =>
    `/agents/${encodeURIComponent(agentId)}/tool-authority`,

  /** The caller's OWN effective authority on the Agent, for authority-shaped UI. */
  EFFECTIVE_AUTHORITY: (agentId: string) =>
    `/agents/${encodeURIComponent(agentId)}/effective-authority`,

  /** The resources a developer may share for one resource-bound Tool (L7). */
  TOOL_RESOURCES: (agentId: string, toolKey: string) =>
    `/agents/${encodeURIComponent(agentId)}/tool-permissions/${encodeURIComponent(
      toolKey
    )}/resources`,

  /** Share/un-share one exact resource for a resource-bound Tool (L7). */
  TOOL_RESOURCE_ITEM: (agentId: string, toolKey: string, resourceId: string) =>
    `/agents/${encodeURIComponent(agentId)}/tool-permissions/${encodeURIComponent(
      toolKey
    )}/resources/${encodeURIComponent(resourceId)}`,

  /**
   * Proxy-relative streaming execute path (no leading slash, no `/api/v1`).
   * Consumed by `workflow-execution-service.ts` via
   * `apiConfigUtils.getStreamingEndpointUrl(...)`. Never issued through Axios.
   */
  EXECUTE_MESSAGE_PATH: (agentId: string, sessionId: string) =>
    `agents/${encodeURIComponent(agentId)}/workflow-sessions/${encodeURIComponent(
      sessionId
    )}/messages`,
} as const;

/**
 * Agent Skill endpoints (Skills 1): Agent-scoped versioned procedures.
 */
export const SKILL_ENDPOINTS = {
  SKILLS: (agentId: string) => `/agents/${encodeURIComponent(agentId)}/skills`,
  SKILL_BY_ID: (agentId: string, skillId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(
      skillId
    )}`,
  VERSIONS: (agentId: string, skillId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(
      skillId
    )}/versions`,
  VERSION_BY_ID: (agentId: string, skillId: string, versionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(
      skillId
    )}/versions/${encodeURIComponent(versionId)}`,
  VERSION_ACTIVATE: (agentId: string, skillId: string, versionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(
      skillId
    )}/versions/${encodeURIComponent(versionId)}/activate`,
  ARCHIVE: (agentId: string, skillId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(
      skillId
    )}/archive`,
  RESTORE: (agentId: string, skillId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(
      skillId
    )}/restore`,
  // Skills 1.1: Chat-generated Skill drafts (session-scoped generation,
  // Agent-scoped reload/regeneration). The browser sends only the session
  // scope + optional focus — never conversation content.
  SESSION_SKILL_DRAFTS: (agentId: string, sessionId: string) =>
    `/agents/${encodeURIComponent(agentId)}/chat-sessions/${encodeURIComponent(
      sessionId
    )}/skill-drafts`,
  SKILL_DRAFT_BY_ID: (agentId: string, draftId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skill-drafts/${encodeURIComponent(
      draftId
    )}`,
  SKILL_DRAFT_REGENERATE: (agentId: string, draftId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skill-drafts/${encodeURIComponent(
      draftId
    )}/regenerate`,
  // Metrics 1.0: version-aware read-only analytics for one Skill.
  ANALYTICS: (agentId: string, skillId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(
      skillId
    )}/analytics`,
} as const;

/**
 * Organisation Shared Skills endpoints (Skills Sharing 1.0): the
 * organisation library, reviewed publication drafts, exact-version
 * installation, and explicit shared-update imports.
 */
export const SHARED_SKILL_ENDPOINTS = {
  SHARED_SKILLS: (organizationId: string) =>
    `/organizations/${encodeURIComponent(organizationId)}/shared-skills`,
  SHARED_SKILL_BY_ID: (organizationId: string, sharedSkillId: string) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skills/${encodeURIComponent(sharedSkillId)}`,
  VERSIONS: (organizationId: string, sharedSkillId: string) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skills/${encodeURIComponent(sharedSkillId)}/versions`,
  VERSION_BY_ID: (
    organizationId: string,
    sharedSkillId: string,
    versionId: string
  ) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skills/${encodeURIComponent(
      sharedSkillId
    )}/versions/${encodeURIComponent(versionId)}`,
  VERSION_ACTIVATE: (
    organizationId: string,
    sharedSkillId: string,
    versionId: string
  ) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skills/${encodeURIComponent(
      sharedSkillId
    )}/versions/${encodeURIComponent(versionId)}/activate`,
  VERSION_COMPATIBILITY: (
    organizationId: string,
    sharedSkillId: string,
    versionId: string
  ) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skills/${encodeURIComponent(
      sharedSkillId
    )}/versions/${encodeURIComponent(versionId)}/compatibility`,
  VERSION_INSTALL: (
    organizationId: string,
    sharedSkillId: string,
    versionId: string
  ) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skills/${encodeURIComponent(
      sharedSkillId
    )}/versions/${encodeURIComponent(versionId)}/install`,
  // Metrics 1.0: exact-provenance Shared release analytics (publishers).
  ANALYTICS: (organizationId: string, sharedSkillId: string) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skills/${encodeURIComponent(sharedSkillId)}/analytics`,
  ARCHIVE: (organizationId: string, sharedSkillId: string) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skills/${encodeURIComponent(sharedSkillId)}/archive`,
  RESTORE: (organizationId: string, sharedSkillId: string) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skills/${encodeURIComponent(sharedSkillId)}/restore`,
  PUBLICATIONS: (organizationId: string) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skill-publications`,
  PUBLICATIONS_FROM_SHARED: (organizationId: string) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skill-publications/from-shared`,
  PUBLICATION_BY_ID: (organizationId: string, draftId: string) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skill-publications/${encodeURIComponent(draftId)}`,
  PUBLICATION_ACTION: (
    organizationId: string,
    draftId: string,
    action: "submit" | "request-changes" | "reject" | "publish" | "cancel"
  ) =>
    `/organizations/${encodeURIComponent(
      organizationId
    )}/shared-skill-publications/${encodeURIComponent(draftId)}/${action}`,
  SHARED_LINEAGE: (agentId: string, skillId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(
      skillId
    )}/shared-lineage`,
  SHARED_UPDATE: (agentId: string, skillId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(
      skillId
    )}/shared-update`,
  SHARED_UPDATE_IMPORT: (agentId: string, skillId: string) =>
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(
      skillId
    )}/shared-update/import`,
} as const;

/**
 * Chat session and message endpoints
 */
export const CHAT_ENDPOINTS = {
  MESSAGES: (agentId: string, sessionId: string) =>
    `/agents/${agentId}/sessions/${sessionId}/messages`,
  MESSAGE_BY_ID: (agentId: string, sessionId: string, messageId: string) =>
    `/agents/${agentId}/sessions/${sessionId}/messages/${messageId}`,
  RETRY_MESSAGE: (agentId: string, sessionId: string, messageId: string) =>
    `/agents/${agentId}/sessions/${sessionId}/messages/${messageId}/retry`,
  EDIT_MESSAGE: (agentId: string, sessionId: string, messageId: string) =>
    `/agents/${agentId}/sessions/${sessionId}/messages/${messageId}/edit`,
  MESSAGE_VERSIONS: (
    agentId: string,
    sessionId: string,
    messageId: string,
    version: number
  ) =>
    `/agents/${agentId}/sessions/${sessionId}/messages/${messageId}/versions/${version}`,
  REVERT_MESSAGE: (
    agentId: string,
    sessionId: string,
    messageId: string,
    version: number
  ) =>
    `/agents/${agentId}/sessions/${sessionId}/messages/${messageId}/versions/${version}/revert`,
} as const;

/**
 * Google services endpoints
 */
export const GOOGLE_ENDPOINTS = {
  AUTH_INITIATE: "/auth/gdrive/initiate-auth",
  AUTH_STATUS: "/auth/gdrive/auth-status",
  PICKER_TOKEN: "/auth/gdrive/picker-token",
  REVOKE_AUTH: "/auth/gdrive/revoke-auth",
  PICKER_CONFIG: "/google/picker/config",
  PICKER_VALIDATE_ACCESS: "/google/picker/validate-access",
} as const;

/**
 * File management endpoints
 */
export const FILE_ENDPOINTS = {
  PRESIGNED_UPLOAD: "/files/presigned-upload",
  LIST: "/files/list",
  DELETE: "/files/delete",
} as const;

/**
 * Admin endpoints
 */
export const ADMIN_ENDPOINTS = {
  // User management
  USERS: "/admin/users",
  USER_BY_ID: (userId: string) => `/admin/users/${userId}`,
  TOGGLE_ORG_ADMIN: (userId: string) =>
    `/admin/users/${userId}/toggle-org-admin`,
  TOGGLE_SUPERUSER: (userId: string) =>
    `/admin/users/${userId}/toggle-superuser`,
  BULK_UPDATE_USERS: "/admin/users/bulk-update",
  BULK_DELETE_USERS: "/admin/users/bulk-delete",
  BULK_MOVE_USERS: "/admin/users/bulk-move-organization",
  BULK_CREATE_USERS: "/admin/users/bulk-create",

  // Organization management
  ORGANIZATIONS: "/admin/organizations",
  ORGANIZATION_BY_ID: (orgId: string) => `/admin/organizations/${orgId}`,
  ORGANIZATION_ANALYTICS: (orgId: string) =>
    `/admin/organizations/${orgId}/analytics`,
  CREATE_ORGANIZATION_WITH_ADMIN: "/admin/organizations/with-admin",

  // Agent management
  AGENTS: "/admin/agents",
  AGENT_STATS: (agentId: string) => `/admin/agents/${agentId}/stats`,

  // System
  SYSTEM_STATUS: "/admin/system-status",
  DASHBOARD_SUMMARY: "/admin/dashboard/summary",
  DEFAULT_AGENT_INFO: "/admin/default-agent-info",

  // MCP Tools (per-agent)
  MCP_TOOLS: (agentId: string) => `/agents/${agentId}/mcp-tools`,
  MCP_TOOL_BY_ID: (agentId: string, toolId: string) =>
    `/agents/${agentId}/mcp-tools/${toolId}`,
  MCP_TOOL_ENABLE: (agentId: string, toolId: string) =>
    `/agents/${agentId}/mcp-tools/${toolId}/enable`,
  MCP_TOOL_DISABLE: (agentId: string, toolId: string) =>
    `/agents/${agentId}/mcp-tools/${toolId}/disable`,
  MCP_TOOL_SYNC_SCHEMA: (agentId: string, toolId: string) =>
    `/agents/${agentId}/mcp-tools/${toolId}/sync-schema`,
  MCP_TOOL_SCHEMA: (agentId: string, toolId: string) =>
    `/agents/${agentId}/mcp-tools/${toolId}/schema`,
  MCP_TOOL_TEST: (agentId: string, toolId: string) =>
    `/agents/${agentId}/mcp-tools/${toolId}/test`,
  MCP_TOOL_INVOKE: (agentId: string, toolId: string) =>
    `/agents/${agentId}/mcp-tools/${toolId}/invoke`,

  // MCP Servers (per-agent)
  MCP_SERVERS: (agentId: string) => `/agents/${agentId}/mcp-servers`,
  MCP_SERVER_BY_ID: (agentId: string, serverId: string) =>
    `/agents/${agentId}/mcp-servers/${serverId}`,
  MCP_SERVER_ENABLE: (agentId: string, serverId: string) =>
    `/agents/${agentId}/mcp-servers/${serverId}/enable`,
  MCP_SERVER_DISABLE: (agentId: string, serverId: string) =>
    `/agents/${agentId}/mcp-servers/${serverId}/disable`,
  MCP_SERVER_TEST: (agentId: string, serverId: string) =>
    `/agents/${agentId}/mcp-servers/${serverId}/test`,
} as const;

/**
 * Public configuration endpoints
 */
export const PUBLIC_CONFIG_ENDPOINTS = {
  CONFIG: "/public-config",
} as const;

/**
 * Utility function to check if an endpoint is public (doesn't require authentication)
 */
export function isPublicEndpoint(url: string): boolean {
  return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
}
